import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import CtaSection from '../components/CtaSection';
import Accordion from '../components/Accordion';
import { useScrollSpy } from '../components/TechDocLayout';
import { Label, Mullions } from '../components/System';
import useSeo from '../lib/useSeo';

// Sidwell is a proof of concept right now (see Terms), so several answers
// below say that plainly rather than papering over it.
const GROUPS = [
  {
    id: 'getting-started',
    label: 'Getting started',
    items: [
      { q: 'What can I connect?', a: 'PostgreSQL, MySQL, or a CSV/Excel upload. Snowflake is planned.' },
      { q: 'Do I need to prepare my data?', a: 'No. Sidwell labels each column for you. Fix any mistake in one click.' },
      { q: 'No database?', a: 'Upload a spreadsheet. It works the same way.' },
    ],
  },
  {
    id: 'data-and-security',
    label: 'Data and security',
    items: [
      { q: 'Does my data leave my database?', a: 'No. The maths runs there. Only a few summary rows come back.' },
      { q: 'What happens to my password?', a: 'It is encrypted on entry and never shown again. Not even to us.' },
      { q: 'Is my data used to train AI?', a: 'No. The AI only sees the small summary, never your raw rows.' },
      { q: 'Can another company see my data?', a: 'No. Every firm is walled off from every other.' },
    ],
  },
  {
    id: 'trusting-the-answer',
    label: 'Trusting the answer',
    items: [
      { q: 'How do I know it is right?', a: 'Every answer has a receipt: the calculation, the data used, and a reference. The AI writes the summary. It never does the maths.' },
      { q: 'What if it misreads my question?', a: 'Check the receipt, then rephrase. Nothing is written to your database, so there is nothing to undo.' },
    ],
  },
  {
    id: 'this-stage',
    label: 'This stage of the product',
    items: [
      { q: 'Is it ready for production data?', a: 'Not yet. Sidwell is an early product. Start with a read-only connection or a sample export.' },
      { q: 'What does it cost?', a: 'Not published yet. Request a demo and we will find the right fit.' },
      { q: 'Another question?', a: 'Email hello@sidwell.ai. A real person reads it.' },
    ],
  },
];

// Mirrors the visible questions, which is what search engines require of FAQ markup.
const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: GROUPS.flatMap((group) => group.items).map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
};

function FAQ() {
  const active = useScrollSpy(GROUPS);

  useSeo({
    title: 'Frequently Asked Questions',
    path: '/faq',
    description: 'Answers on what Sidwell connects to, whether data leaves your database, whether it trains AI, how to check an answer, and what stage the product is at.',
    schema: FAQ_SCHEMA,
  });

  return (
    <main className="relative w-full">
      <PageHero
        variant="memo"
        image="colonnade"
        eyebrow="FAQ"
        title="Straight answers."
        subtitle="What it connects to, what happens to your data, and where we are."
        badge="Still in testing"
      />

      <section className="relative bg-limestone">
        <Mullions />
        <div className="shell">
          <div className="grid-12 relative gap-y-8 pb-20 pt-10 lg:pb-32 lg:pt-16">
            <nav aria-label="Question categories" className="col-span-4 lg:col-span-3">
              <div className="lg:sticky lg:top-[96px]">
                <Label muted className="mb-4 hidden lg:block">Categories</Label>
                <ol className="m-0 flex list-none flex-wrap gap-x-5 border-y border-wall/15 p-0 lg:block lg:border-t-0">
                  {GROUPS.map((g) => {
                    const on = active === g.id;
                    return (
                      <li key={g.id} className="lg:border-t lg:border-wall/15">
                        <a
                          href={`#${g.id}`}
                          aria-current={on ? 'location' : undefined}
                          className={`relative flex min-h-[44px] items-center py-2 text-[15px] leading-snug transition-colors lg:pl-3 ${
                            on ? 'font-semibold text-wall' : 'text-glass hover:text-wall'
                          }`}
                        >
                          <span aria-hidden="true" className={`absolute inset-y-0 left-0 hidden w-[2px] lg:block ${on ? 'bg-bronze' : 'bg-transparent'}`} />
                          {g.label}
                        </a>
                      </li>
                    );
                  })}
                </ol>
              </div>
            </nav>
            <div className="col-span-4 min-w-0 lg:col-span-8 lg:col-start-5">
              {GROUPS.map((group) => (
                <section key={group.id} id={group.id} className="pb-14 last:pb-0">
                  <h2 className="type-h2 mb-5 !text-[clamp(28px,2.8vw,42px)]">{group.label}</h2>
                  <div className="border-t border-wall/70">
                    {group.items.map((item) => (
                      <Accordion key={item.q} title={item.q} detail={item.a} />
                    ))}
                  </div>
                </section>
              ))}
              <p className="mt-12 border-t border-wall/25 pt-5 text-[15px] text-glass">
                More questions? Email <a className="link-inline" href="mailto:hello@sidwell.ai">hello@sidwell.ai</a> or see{' '}
                <Link className="link-inline" to="/security">Security &amp; Privacy</Link> and{' '}
                <Link className="link-inline" to="/terms">Terms of Service</Link> pages.
              </p>
            </div>
          </div>
        </div>
      </section>

      <CtaSection
        image="facade"
        eyebrow="Get Started"
        title="Try your own question."
        subtitle="We’ll show you the receipt behind the answer."
        primary={{ label: 'Request a demo', to: '/#request-briefing' }}
        secondary={{ label: 'Back to Overview', to: '/' }}
      />
    </main>
  );
}

export default FAQ;
